import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

/**
 * TypeSpecificFields Component
 * Dynamically renders product fields based on store type
 */
export function TypeSpecificFields({ storeType, formData, setFormData }) {
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      type_specific_data: {
        ...(prev.type_specific_data || {}),
        [field]: value
      }
    }));
  };

  const getValue = (field) => {
    return formData?.type_specific_data?.[field] || '';
  };

  // Field configurations for each store type
  const fieldConfigs = {
    digital: [
      { name: 'download_url', label: 'Download URL', type: 'url', placeholder: 'https://...' },
      { name: 'file_size', label: 'File Size (MB)', type: 'number', placeholder: '10' },
      { name: 'file_type', label: 'File Type', type: 'text', placeholder: 'PDF, ZIP, MP3, etc.' },
      { name: 'license_type', label: 'License Type', type: 'text', placeholder: 'Single-user, Multi-user, etc.' },
      { name: 'download_limit', label: 'Download Limit', type: 'number', placeholder: '5' },
      { name: 'version', label: 'Version', type: 'text', placeholder: '1.0.0' }
    ],
    academy: [
      { name: 'duration_hours', label: 'Duration (hours)', type: 'number', placeholder: '20', required: true },
      { name: 'lesson_count', label: 'Number of Lessons', type: 'number', placeholder: '15', required: true },
      { name: 'instructor_name', label: 'Instructor Name', type: 'text', placeholder: 'John Doe', required: true },
      { name: 'certificate_offered', label: 'Certificate Offered', type: 'select', options: ['Yes', 'No'] },
      { name: 'prerequisites', label: 'Prerequisites', type: 'text', placeholder: 'Basic programming knowledge' },
      { name: 'skill_level', label: 'Skill Level', type: 'select', options: ['Beginner', 'Intermediate', 'Advanced'] },
      { name: 'enrollment_limit', label: 'Enrollment Limit', type: 'number', placeholder: '50' }
    ],
    tutor: [
      { name: 'session_duration', label: 'Session Duration (mins)', type: 'number', placeholder: '60', required: true },
      { name: 'subjects', label: 'Subjects', type: 'text', placeholder: 'Math, Physics, Chemistry', required: true },
      { name: 'hourly_rate', label: 'Hourly Rate', type: 'number', placeholder: '250', required: true },
      { name: 'experience_years', label: 'Experience (years)', type: 'number', placeholder: '5' },
      { name: 'qualifications', label: 'Qualifications', type: 'text', placeholder: 'PhD in Mathematics' },
      { name: 'location_type', label: 'Location', type: 'select', options: ['Online', 'In-person', 'Both'] },
      { name: 'availability_slots', label: 'Availability', type: 'text', placeholder: 'Mon-Fri 9am-5pm' }
    ],
    services: [
      { name: 'service_duration', label: 'Service Duration (mins)', type: 'number', placeholder: '120', required: true },
      { name: 'location', label: 'Service Location', type: 'text', placeholder: 'Customer location', required: true },
      { name: 'booking_required', label: 'Booking Required', type: 'select', options: ['Yes', 'No'] },
      { name: 'service_area', label: 'Service Area', type: 'text', placeholder: 'Johannesburg, Pretoria' },
      { name: 'team_size', label: 'Team Size', type: 'number', placeholder: '2' },
      { name: 'availability_slots', label: 'Availability', type: 'text', placeholder: 'Weekdays 8am-6pm' }
    ],
    food: [
      { name: 'ingredients', label: 'Ingredients', type: 'textarea', placeholder: 'Tomatoes, cheese, flour...', required: true },
      { name: 'allergens', label: 'Allergens', type: 'text', placeholder: 'Dairy, gluten, nuts' },
      { name: 'prep_time_mins', label: 'Prep Time (mins)', type: 'number', placeholder: '30', required: true },
      { name: 'delivery_time_mins', label: 'Delivery Time (mins)', type: 'number', placeholder: '45' },
      { name: 'dietary_tags', label: 'Dietary Tags', type: 'text', placeholder: 'Vegan, Halal, Gluten-free' },
      { name: 'spice_level', label: 'Spice Level', type: 'select', options: ['Mild', 'Medium', 'Hot', 'Extra Hot'] },
      { name: 'menu_category', label: 'Menu Category', type: 'text', placeholder: 'Appetizer, Main, Dessert', required: true }
    ],
    property: [
      { name: 'bedrooms', label: 'Bedrooms', type: 'number', placeholder: '3', required: true },
      { name: 'bathrooms', label: 'Bathrooms', type: 'number', placeholder: '2', required: true },
      { name: 'square_feet', label: 'Square Feet', type: 'number', placeholder: '1500', required: true },
      { name: 'property_type', label: 'Property Type', type: 'select', options: ['For Sale', 'For Rent'], required: true },
      { name: 'amenities', label: 'Amenities', type: 'text', placeholder: 'Pool, Gym, Parking' },
      { name: 'year_built', label: 'Year Built', type: 'number', placeholder: '2015' },
      { name: 'parking_spaces', label: 'Parking Spaces', type: 'number', placeholder: '2' },
      { name: 'furnished', label: 'Furnished', type: 'select', options: ['Yes', 'No', 'Partially'] }
    ],
    marketplace: [] // Generic marketplace - no type-specific fields
  };

  const fields = fieldConfigs[storeType] || [];

  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-sm">
          {storeType.charAt(0).toUpperCase() + storeType.slice(1)}-Specific Details
        </h3>
        <Badge variant="secondary" className="text-xs">Optional but Recommended</Badge>
      </div>
      
      <div className="grid gap-3 md:grid-cols-2">
        {fields.map(field => (
          <div key={field.name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
            <Label htmlFor={field.name} className="text-xs flex items-center gap-1">
              {field.label}
              {field.required && <span className="text-red-500">*</span>}
            </Label>
            
            {field.type === 'select' ? (
              <select
                id={field.name}
                value={getValue(field.name)}
                onChange={(e) => handleChange(field.name, e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
              >
                <option value="">Select...</option>
                {field.options?.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : field.type === 'textarea' ? (
              <textarea
                id={field.name}
                value={getValue(field.name)}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background"
                rows={3}
              />
            ) : (
              <Input
                id={field.name}
                type={field.type}
                value={getValue(field.name)}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                className="mt-1 text-sm"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
